import { useState } from 'react';
import { adminApi } from '../../services/api';
import { SkillLevel, Verbosity, type UpdateUserProfileRequest } from '../../types/admin';
import type { User } from '../../types/api';

interface UserProfileFormProps {
  user: User;
  userId: string;
  onUpdate: () => void;
}

const SkillLevelOptions = [
  { value: SkillLevel.Beginner, label: 'Beginner' },
  { value: SkillLevel.Intermediate, label: 'Intermediate' },
  { value: SkillLevel.Advanced, label: 'Advanced' }
];

const VerbosityOptions = [
  { value: Verbosity.Brief, label: 'Brief' },
  { value: Verbosity.Moderate, label: 'Moderate' },
  { value: Verbosity.Detailed, label: 'Detailed' }
];

export default function UserProfileForm({ user, userId, onUpdate }: UserProfileFormProps) {
  const [formData, setFormData] = useState({
    displayName: user.displayName,
    skillLevel: user.skillLevel ?? SkillLevel.Beginner,
    preferredVerbosity: user.preferredVerbosity ?? Verbosity.Moderate,
    monthlyMessageLimit: user.monthlyMessageLimit,
    isActive: user.isActive,
    preferences: user.preferences || '',
    notes: user.notes || '',
    rules: user.rules || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (formData.monthlyMessageLimit < 1) {
      setError('Message limit must be at least 1');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const updateData: UpdateUserProfileRequest = {
        displayName: formData.displayName.trim(),
        skillLevel: formData.skillLevel,
        preferredVerbosity: formData.preferredVerbosity,
        monthlyMessageLimit: formData.monthlyMessageLimit,
        isActive: formData.isActive,
        preferences: formData.preferences.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        rules: formData.rules.trim() || undefined
      };

      await adminApi.updateUserProfile(userId, updateData);
      setSuccess(true);
      onUpdate();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    return formData.displayName !== user.displayName ||
           formData.skillLevel !== (user.skillLevel ?? SkillLevel.Beginner) ||
           formData.preferredVerbosity !== (user.preferredVerbosity ?? Verbosity.Moderate) ||
           formData.monthlyMessageLimit !== user.monthlyMessageLimit ||
           formData.isActive !== user.isActive ||
           formData.preferences !== (user.preferences || '') ||
           formData.notes !== (user.notes || '') ||
           formData.rules !== (user.rules || '');
  };

  return (
    <div className="profile-form-container">
      <h2>User Profile Settings</h2>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">Profile updated successfully!</div>}

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-group">
          <label htmlFor="displayName">Display Name</label>
          <input
            id="displayName"
            type="text"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            required
          />
        </div>

        <div className="form-section">
          <h3>AI Personalization</h3>
          
          <div className="form-group">
            <label htmlFor="skillLevel">Technical Skill Level</label>
            <select
              id="skillLevel"
              value={formData.skillLevel}
              onChange={(e) => setFormData({ ...formData, skillLevel: Number(e.target.value) })}
            >
              {SkillLevelOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="form-help">Controls technical language complexity in AI responses</p>
          </div>

          <div className="form-group">
            <label htmlFor="verbosity">Preferred Response Length</label>
            <select
              id="verbosity"
              value={formData.preferredVerbosity}
              onChange={(e) => setFormData({ ...formData, preferredVerbosity: Number(e.target.value) })}
            >
              {VerbosityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="form-help">Controls how detailed AI responses should be</p>
          </div>
        </div>

        <div className="form-section">
          <h3>User Context</h3>
          
          <div className="form-group">
            <label htmlFor="preferences">Preferences</label>
            <textarea
              id="preferences"
              value={formData.preferences}
              onChange={(e) => setFormData({ ...formData, preferences: e.target.value })}
              placeholder="e.g., Prefers visual guides, dislikes phone calls, available evenings only"
              rows={3}
            />
            <p className="form-help">Communication style, scheduling preferences, etc.</p>
          </div>

          <div className="form-group">
            <label htmlFor="notes">General Notes</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="e.g., Has poor eyesight, lives in rural area with slow internet"
              rows={3}
            />
            <p className="form-help">Tech environment or context that affects assistance</p>
          </div>

          <div className="form-group">
            <label htmlFor="rules">Assistance Rules</label>
            <textarea
              id="rules"
              value={formData.rules}
              onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
              placeholder="e.g., Don't recommend Apple products, avoid suggesting hardware purchases"
              rows={3}
            />
            <p className="form-help">Specific constraints or preferences for AI guidance</p>
          </div>
        </div>

        <div className="form-section">
          <h3>Account Settings</h3>
          
          <div className="form-group">
            <label htmlFor="messageLimit">Monthly Message Limit</label>
            <input
              id="messageLimit"
              type="number"
              min="1"
              value={formData.monthlyMessageLimit}
              onChange={(e) => setFormData({ ...formData, monthlyMessageLimit: Number(e.target.value) })}
              required
            />
            <p className="form-help">
              Current usage: {user.messagesUsedThisMonth} / {user.monthlyMessageLimit}
            </p>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span>Account Active</span>
            </label>
            <p className="form-help">Inactive accounts cannot send messages</p>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-primary"
            disabled={saving || !hasChanges()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
